import * as styles from '../FirstResponder.module.css';
import PreviewPagination from './PreviewPagination';
import JobRow from './JobRow';
import { LiveClientFilter, LiveJobRow, LivePage } from './liveViewModel';

/*
 * Jobs card: client filter chips + the jobs table. STATELESS and
 * collection-free: it consumes a SERVER-SHAPED PAGE from the adapter (it
 * never receives the full jobs array and never slices), and both the selected
 * filter and page changes are callbacks to the data owner. Every interactive
 * element is a real native <button>; a <tr> is never an improvised control.
 *
 * "Workers available" is EXACTLY pool_after_gates. A non-null count is an
 * inspectable native button opening the job drilldown on its Workers
 * available section; null renders "—" and is not clickable.
 *
 * GRAVY-10373 Task 7 — a row with `clickable: false` (the real adapter's unrecorded 'watching'
 * rows, which have no persisted run to show — GET .../jobs/:id would 404 for them) renders plain,
 * non-interactive text instead of a button, with a `title` explaining why, and never calls
 * onSelectJob. Never a broken or empty modal.
 *
 * GRAVY-10373 M1 — `loadError` renders a small inline retry notice when a clicked row's job
 * detail/workers failed to load (a failure used to be a silent dead click). Optional and null in
 * the preview, which never fails; only the real connected container supplies it.
 *
 * GRAVY-10373 F8 — `pageError` is the same notice for the OTHER silent failure: the jobs page
 * fetch behind the filter chips and pagination. Two props rather than one because their Retry
 * buttons re-run different requests, and a Retry whose meaning depends on which failure happened
 * is no more trustworthy than the empty catch it replaced.
 */

type Props = {
  page: LivePage<LiveJobRow>; // adapter-produced page — never the full list
  onPageChange: (page: number) => void;
  filters: LiveClientFilter[];
  selectedFilter: string;
  onSelectFilter: (key: string) => void;
  onSelectJob: (
    id: string,
    section?: 'overview' | 'workers',
    runId?: string | null,
  ) => void;
  elapsed: number;
  jobsLoading: boolean;
  loadError?: { message: string; onRetry: () => void } | null;
  pageError?: { message: string; onRetry: () => void } | null;
};

const JobsCard = ({
  page: { items: rows, pagination },
  onPageChange,
  filters,
  selectedFilter,
  onSelectFilter,
  onSelectJob,
  elapsed,
  jobsLoading,
  loadError,
  pageError,
}: Props) => (
  <div className={styles.card}>
    <div className={styles.cardHeader}>
      <h3>Jobs</h3>
    </div>
    {loadError && (
      <div
        className={styles.section}
        data-testid="flp-jobs-load-error"
        role="alert"
      >
        <div className={styles.formErrors}>
          {loadError.message}
          <button
            type="button"
            className={`${styles.btn} ${styles.btnOutline} ${styles.btnSm}`}
            style={{ marginLeft: 8 }}
            onClick={loadError.onRetry}
          >
            Retry
          </button>
        </div>
      </div>
    )}
    {/* F8: the filter/pagination fetch's own failure — its own notice and its own Retry, because
        it retries a different request than loadError's (this page of the table, not a job open).
        The table below keeps rendering whatever page last loaded: a failed fetch reports itself,
        it never takes away what the operator could already read. */}
    {pageError && (
      <div
        className={styles.section}
        data-testid="flp-jobs-page-error"
        role="alert"
      >
        <div className={styles.formErrors}>
          {pageError.message}
          <button
            type="button"
            className={`${styles.btn} ${styles.btnOutline} ${styles.btnSm}`}
            style={{ marginLeft: 8 }}
            onClick={pageError.onRetry}
          >
            Retry
          </button>
        </div>
      </div>
    )}
    <div className={styles.section}>
      <div className={styles.filters}>
        {filters.map((f) => {
          const active = selectedFilter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              className={`${styles.chip} ${active ? styles.chipActive : ''}`}
              data-testid={`flp-filter-${f.key}`}
              data-active={active ? 'true' : 'false'}
              aria-pressed={active}
              disabled={jobsLoading}
              onClick={() => onSelectFilter(f.key)}
            >
              {f.label}
              {f.watching !== null && f.filled !== null && (
                <span className={styles.chipCnt}>
                  {f.watching} watching · {f.filled} filled
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
    {jobsLoading ? (
      <div className={styles.section} data-testid="flp-jobs-loading">
        <span className={styles.muted}>Loading jobs…</span>
      </div>
    ) : rows.length === 0 ? (
      <div className={styles.section} data-testid="flp-jobs-empty">
        <span className={styles.muted}>No jobs in the window.</span>
      </div>
    ) : (
      <>
        <div className={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Job</th>
                <th>Client</th>
                <th>Status</th>
                <th>Trigger</th>
                <th>Workers available</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((j) => (
                // Keyed by the JOB, now genuinely unique (GRAVY-10816 follow-up): a reopened job
                // is one row, its history folded into it, never two rows for one shift.
                <JobRow
                  key={j.id}
                  j={j}
                  onSelectJob={onSelectJob}
                  elapsed={elapsed}
                />
              ))}
            </tbody>
          </table>
        </div>
        <PreviewPagination
          pagination={pagination}
          onPageChange={onPageChange}
          itemsLabel="jobs"
        />
      </>
    )}
  </div>
);

export default JobsCard;
